
import { Col, Divider, Row, Spin, Tag, Typography } from "antd";
import DashboardCard from "../../Common/Card/DashboardCard";
import { useGetAllQuery, useGetUserChartQuery, useGetBookingChartQuery } from "../../../feature/dashboard/dashboardApiSlice";
import { useEffect } from "react";
import React from "react";
import DashboardPlaceCard from "../../Common/Card/DashboardPlaceCard";
import dayjs from 'dayjs';
import AreaChart from "../../DashboardComponent/AreaChart";
import LineChart from "../../DashboardComponent/LineChart";
import { Pie } from "@ant-design/charts";
import styled from "styled-components";
import { LightBlueContainer, WhiteContainer } from "../../styled/Container.styled";
import { useGetAllTransactionQuery } from "../../../feature/Transactions/transactionApiSlice";
import { Link, useLocation } from "react-router-dom";

const user: string = require("../../../assets/logo/user.svg").default;
const driver: string = require("../../../assets/logo/driver.svg").default;

const category: string = require("../../../assets/logo/category.svg").default;

const offer: string = require("../../../assets/logo/offer.svg").default;
const style: React.CSSProperties = { padding: "5px 0" };

const DashboardLightBlueContainer = styled(LightBlueContainer)`
  padding: 5px;
  border: none;
`;

const TransactionLightBlueContainer = styled(LightBlueContainer)`
  padding: 5px;
  border: none;
`;

function DashboardTransactionCard() {
  const { Text } = Typography;
  const { data } = useGetAllTransactionQuery();

  const recentTransactions = [...(data?.data?.content || [])]
    .sort((a: any, b: any) => {
      const dateA = new Date(`${a.dateOfTransaction} ${a.timeOfTransaction}`).getTime();
      const dateB = new Date(`${b.dateOfTransaction} ${b.timeOfTransaction}`).getTime();
      return dateB - dateA;
    })
    .slice(0, 5);

  return (
    <TransactionLightBlueContainer>
      <Text className="dashboard-card-heading-text-size">Recent Transactions</Text>
      <Row
        style={{
          width: "100%",
          minHeight: "320px",
          marginTop: "0.5rem",
          padding: "0.5rem 1rem",
          borderRadius: "10px",
          background: "#fff",
        }}
      >
        {recentTransactions.map((item: any, index: number) => (
          <React.Fragment key={item.transactionId || item.paymentId || index}>
            <Row
              align="middle"
              gutter={[16, 0]}
              style={{ width: "100%", padding: "10px 0" }}
            >
              <Col span={6}>
                <Text strong>{item.driverName || "-"}</Text>
              </Col>
              <Col span={5}>
                <Text>₹{item.transactionAmount}</Text>
              </Col>
              <Col span={5}>
                <Tag color="blue">{item.paymentMode}</Tag>
              </Col>
              <Col span={5}>
                <Text type="secondary">{item.tripType}</Text>
              </Col>
              <Col span={3}>
                <Text type="secondary">{item.dateOfTransaction}</Text>
              </Col>
            </Row>
            {index !== recentTransactions.length - 1 && (
              <Divider style={{ margin: "0" }} />
            )}
          </React.Fragment>
        ))}
        {recentTransactions.length === 0 && (
          <Col span={24} style={{ textAlign: "center", padding: "20px 0" }}>
            <Text type="secondary">No recent transactions</Text>
          </Col>
        )}
      </Row>
    </TransactionLightBlueContainer>
  );
}

function TripTypePieChart() {
  const { Text } = Typography;
  const { data } = useGetAllTransactionQuery();

  const counts: Record<string, number> = {};
  (data?.data?.content || []).forEach((item: any) => {
    const type = item.tripType || "UNKNOWN";
    counts[type] = (counts[type] || 0) + 1;
  });

  const pieData = Object.keys(counts).map((type) => ({
    type,
    value: counts[type],
  }));

  const config = {
    data: pieData,
    height: 280,
    appendPadding: 24,
    angleField: "value",
    colorField: "type",
    radius: 0.95,
    label: {
      type: "spider",
      formatter: (datum: any) => {
        const total = pieData.reduce((sum, d) => sum + d.value, 0);
        const percent = total ? (datum.value / total) * 100 : 0;
        return `${percent.toFixed(0)}%`;
      },
    },
    legend: {
      position: "bottom",
    },
    color: ["#eb2f96", "#4c9eff", "#52c41a"],
  };

  return (
    <DashboardLightBlueContainer>
      <Row justify="space-between">
        <Text className="dashboard-card-heading-text-size">Trip Type Distribution</Text>
      </Row>
      <WhiteContainer
        className="inner-white-container-style"
        style={{ padding: "0.5rem", minHeight: "320px", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        {pieData.length > 0 ? (
          <div style={{ width: "100%" }}>
            <Pie {...(config as any)} />
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#999" }}>
            No transaction data
          </div>
        )}
      </WhiteContainer>
    </DashboardLightBlueContainer>
  );
}

function Dashboard() {
  const { data, refetch } = useGetAllQuery();
  const { data: userData, refetch: fetchUsers } = useGetUserChartQuery(dayjs().year());
  const { data: bookingData, refetch: fetchBookings } = useGetBookingChartQuery(dayjs().year());
  const { pathname } = useLocation();

  useEffect(() => {
    refetch();
    fetchUsers();
    fetchBookings();
  }, [refetch, fetchUsers, fetchBookings]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <>
      {!!data ? (
        <div>
          <Row gutter={[16, 24]}>
  <Col className="gutter-row" span={6}>
    <Link to="/users">
      <div style={style}>
        <DashboardCard
          heading={"Users"}
          subText={"New User"}
          cardIcon={<img src={user} alt="logo" className="img-icon-size" />}
          totalCount={data?.data.usersCount}
          newCount={data?.data.newUserCount}
        />
      </div>
    </Link>
  </Col>
  <Col className="gutter-row" span={6}>
    <Link to="/drivers">
      <div style={style}>
        <DashboardCard
          heading={"Driver"}
          subText={"New Driver"}
          cardIcon={<img src={driver} alt="logo" className="img-icon-size" />}
          newCount={data?.data.newDriverCount}
          totalCount={data?.data.driverCount}
        />
      </div>
    </Link>
  </Col>
  <Col className="gutter-row" span={6}>
    <Link to="/categories">
      <div style={style}>
        <DashboardCard
          heading={"Categories"}
          subText={"New Category"}
          cardIcon={<img src={category} alt="logo" className="img-icon-size" />}
          newCount={data?.data.newCategoriesCount}
          totalCount={data?.data.categoriesCount}
        />
      </div>
    </Link>
  </Col>
  <Col className="gutter-row" span={6}>
    <Link to="/offers">
      <div style={style}>
        <DashboardCard
          heading={"Offers"}
          subText={"New Offer"}
          cardIcon={<img src={offer} alt="logo" className="img-icon-size" />}
          newCount={data?.data.newOfferCount}
          totalCount={data?.data.offersCount}
        />
      </div>
    </Link>
  </Col>
</Row>

          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={16}>
              <div style={style}>
                {" "}
                <AreaChart heading="Users" />
              </div>
              <div style={style}>
                {" "}
                <LineChart heading="Bookings" />
              </div>
            </Col>
            <Col className="gutter-row" span={8}>
            
              <div style={style}>
                <DashboardPlaceCard data={data} heading={"Popular Places"} />
                
              </div>
            </Col>
          </Row>

          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={16}>
              <Link to="/transactions">
                <div style={style}>
                  <DashboardTransactionCard />
                </div>
              </Link>
            </Col>
            <Col className="gutter-row" span={8}>
              <div style={style}>
                <TripTypePieChart />
              </div>
            </Col>
          </Row>
        </div>
      ) : (
        <Spin size="large" />
      
      )}
    </>
  );
}

export default Dashboard;
